import { NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  PreloadAllModules,
  RouteReuseStrategy,
  RouterModule,
  Routes,
} from "@angular/router";
import { ProcessingComponent } from "@modules/shared/processing/processing.component";
import { UnauthorizedComponent } from "@modules/shared/unauthorized/unauthorized.component";
import {
  appRouteAccessPolicy,
  callRoutePath,
} from "./app-route-access-policy";
import { BroadcastDashboardComponent } from "./broadcast/broadcast-dashboard/broadcast-dashboard.component";
import { BroadcastComponent } from "./broadcast/broadcast/broadcast.component";
import { ConversationResolver } from "./conversation.resolver";
import { CanDeactivateGuard } from "./deactivate.guard";
import { developmentRoutes } from "./development/development.routes";
import { ContactComponent } from "./directory/network/contact/contact.component";
import { NetworkComponent } from "./directory/network/network/network.component";
import { WorkspaceComponent } from "./directory/network/workspace/workspace.component";
import { AuthGuard } from "./guards/auth.guard";
import { PinGuard } from "./guards/pin.guard";
import { HomeComponent } from "./home/home.component";
import { LoggedInOidcComponent } from "./logged-in-oidc/logged-in-oidc.component";
import { SetPasswordComponent } from "./login-screen/set-password/set-password.component";
import { LoginComponent } from "./login/login.component";
import { LogoutOidcComponent } from "./logout-oidc/logout-oidc.component";
import { NotificationsComponent } from "./modules/notifications/notifications/notifications.component";
import { WorkspaceAddComponent } from "./onboard/workspace-add/workspace-add.component";
import { PageNotFoundComponent } from "./page-not-found/page-not-found.component";
import { PinscreenComponent } from "./pinscreen/pinscreen.component";
import { EditComponent } from "./profile/edit/edit.component";
import { RegisterLoginOidcComponent } from "./register-login-oidc/register-login-oidc.component";
import { AdminDashboardComponent } from "./roles/admin-dashboard/admin-dashboard.component";
import { RolesPanelComponent } from "./roles/roles-panel/roles-panel.component";
import { SettingsComponent } from "./settings/settings.component";
import { callDeactivateGuard } from "./voip/call-deactivate.guard";
import { CallComponent } from "./voip/call/call.component";

class CustomRouteReuseStrategy extends BaseRouteReuseStrategy {
  public shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    // If the user is already on the call screen (i.e. they accepted to join another call while on the call screen)
    // then we force the call screen to reload to ensure all video call related elements are reconstructed.
    // This is in line with the Zoom SDK's best practices which suggest destroying the VideoClient after leaving a call.
    if (future.routeConfig?.path === callRoutePath) {
      return false;
    }
    return super.shouldReuseRoute(future, curr);
  }
}

export const appRoutes: Routes = [
  {
    ...appRouteAccessPolicy.admin,
    component: RolesPanelComponent,
    children: [
      {
        redirectTo: "dashboard",
        path: "",
        pathMatch: "full",
      },
      {
        path: "dashboard",
        pathMatch: "full",
        component: AdminDashboardComponent,
      },
    ],
  },
  {
    ...appRouteAccessPolicy.broadcast,
    children: [
      {
        redirectTo: "dashboard",
        path: "",
        pathMatch: "full",
      },
      {
        path: "new",
        pathMatch: "full",
        component: BroadcastComponent,
      },
      {
        path: "dashboard",
        component: BroadcastDashboardComponent,
      },
    ],
  },
  {
    path: "secure",
    loadChildren: () =>
      import("./modules/library/library.module").then((m) => m.LibraryModule),
  },
  {
    path: "conversations",
    loadChildren: () =>
      import("./modules/conversations/conversations.module").then(
        (m) => m.ConversationsModule
      ),
  },
  {
    path: "roles",
    loadChildren: () =>
      import("./modules/roles/roles.module").then((m) => m.RolesModule),
  },
  {
    ...appRouteAccessPolicy.network,
    component: NetworkComponent,
    children: [
      {
        path: "contact/:contactId",
        component: ContactComponent,
      },
      {
        path: "workspace/:workspaceId",
        component: WorkspaceComponent,
      },
      {
        path: "workspace",
        component: WorkspaceComponent,
      },
    ],
  },
  {
    ...appRouteAccessPolicy.settings,
    component: SettingsComponent,
  },
  {
    path: "invite",
    redirectTo: "",
  },
  {
    ...appRouteAccessPolicy.edit,
    component: EditComponent,
  },
  {
    ...appRouteAccessPolicy.profile,
    loadChildren: () =>
      import("./modules/profile/profile.module").then((m) => m.ProfileModule),
  },
  {
    ...appRouteAccessPolicy.notifications,
    component: NotificationsComponent,
  },
  {
    path: "login",
    component: LoginComponent,
  },
  {
    path: "unauthorized",
    redirectTo: "unauthorised",
    pathMatch: "full",
  },
  {
    path: "unauthorised",
    component: UnauthorizedComponent,
  },
  {
    path: "signin-oidc",
    component: LoggedInOidcComponent,
  },
  {
    path: "register-login-oidc",
    component: RegisterLoginOidcComponent,
  },
  {
    path: "signout-callback-oidc",
    component: LogoutOidcComponent,
  },
  {
    ...appRouteAccessPolicy.pin,
    component: PinscreenComponent,
  },
  {
    ...appRouteAccessPolicy.setPassword,
    component: SetPasswordComponent,
  },
  {
    ...appRouteAccessPolicy.processing,
    component: ProcessingComponent,
  },
  {
    ...appRouteAccessPolicy.workspaceAdd,
    component: WorkspaceAddComponent,
  },
  {
    path: "external",
    loadChildren: () =>
      import("./modules/external/external.module").then(
        (m) => m.ExternalModule
      ),
  },
  {
    path: "scribe",
    loadChildren: () =>
      import("./modules/scribe/scribe.module").then((m) => m.ScribeModule),
  },
  {
    ...appRouteAccessPolicy.call,
    component: CallComponent,
    canDeactivate: [callDeactivateGuard],
    title: "Celo Call",
  },
  ...developmentRoutes,
  {
    path: "",
    component: HomeComponent,
  },
  {
    path: "**",
    component: PageNotFoundComponent,
    data: {
      isNotFound: true,
    },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: "enabled",
      preloadingStrategy: PreloadAllModules,
    }),
  ],
  exports: [RouterModule],
  providers: [
    AuthGuard,
    PinGuard,
    ConversationResolver,
    CanDeactivateGuard,
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
  ],
})
export class AppRoutingModule {}
